import axios from 'axios'
import env from '@shared/env.js'

// Separate from PublicApi and PrivateApi, whose interceptors send the guid and auth headers,
// write to the auth store and log failed request URLs. An action link works without a login,
// and its alias must stay out of logs.
const actionTokenApi = axios.create({ baseURL: env.VITE_API_BASE_URL })

export const ERRORS = {
  INVALID: 'invalid',
  RETRY: 'retry',
}

// Carries only the kind of failure: the axios error would keep the request, and with it the alias.
export class ActionLinkError extends Error {
  constructor(kind) {
    super(`Action link request failed: ${kind}`)
    this.name = 'ActionLinkError'
    this.kind = kind
  }
}

const toActionLinkError = (error) => {
  const status = error.response?.status
  // A 404 means the backend turned action links off, and turning them off cancels every link
  if (status === 401 || status === 404)
    return new ActionLinkError(ERRORS.INVALID)
  // Like the app, the pages show one error for every other failure
  return new ActionLinkError(ERRORS.RETRY)
}

export const validate = async (alias, purpose) => {
  try {
    await actionTokenApi.post('v2/action_tokens/validate', {
      token_alias: alias,
      purpose,
    })
  } catch (error) {
    throw toActionLinkError(error)
  }
}

export const upload = async (alias, file) => {
  const formData = new FormData()
  formData.append('token_alias', alias)
  formData.append('file', file)
  // The view only uploads after the user agreed in the confirmation sheet
  formData.append('accepted_file_processing_agreement', 'true')

  try {
    await actionTokenApi.post('v2/action_tokens/prescriptions', formData)
  } catch (error) {
    throw toActionLinkError(error)
  }
}

// A search the user typed past is canceled through the signal, and that is not a failure to map.
export const searchPractices = async (alias, name, signal) => {
  try {
    const { data } = await actionTokenApi.post(
      'v2/action_tokens/doctor_practices/search',
      { token_alias: alias, name },
      { signal },
    )
    return data
  } catch (error) {
    if (axios.isCancel(error)) throw error
    throw toActionLinkError(error)
  }
}

export const requestDoctorOutreach = async (alias, payload) => {
  try {
    await actionTokenApi.post('v2/action_tokens/doctor_outreach_requests', {
      token_alias: alias,
      ...payload,
    })
  } catch (error) {
    throw toActionLinkError(error)
  }
}
